import "./progress-circle.less";
import {clamp, classNames} from "utils/helpers";
import { useEffect, useMemo, useState } from "react";

interface Props {
  /** Number between 1-100 */
  percent: number
  className?: string
  correct: boolean
}


// https://easings.net/#easeOutCubic
function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

// https://easings.net/#easeOutQuint
function easeOutQuint(x: number): number {
  return 1 - Math.pow(1 - x, 5);
}

// https://newbedev.com/ease-in-and-ease-out-animation-formula
function intOutQuadBlend(t: number): number {
  if (t <= 0.5)
    return 2.0 * t * t
  t -= 0.5
  return 2.0 * t * (1.0 - t) + 0.5
}

// https://newbedev.com/ease-in-and-ease-out-animation-formula
function bezierBlend(t: number): number {
  return t * t * (3.0 - 2.0 * t)
}

// https://newbedev.com/ease-in-and-ease-out-animation-formula
function parametricBlend(t: number): number {
  const sqt = t * t
  return sqt / (2.0 * (sqt - t) + 1.0)
}

const ANIMATION_MS_FOR_100_PERCENT = 5000

export default function ProgressCircle(props: Props) {
  const [progress, setProgress] = useState(0.0)
  const { percent, correct, className } = props
  const maxText = Math.round(percent)

  const [path, text] = useMemo(() => {
    // console.log('xx')
    const finalTarget = percent * 0.01
    let q = progress * finalTarget
    let text = clamp(Math.round(progress * percent), 0, maxText);
    
    const CAP = 0.9999999
    if (q >= CAP) {
      q = CAP
    }
    
    let sweepRadian = 2*Math.PI*q
    
    const path = buildArcPath({
        center: { x: 50, y: 50 },
        radius: { x: 43, y: 43 },
        startAngleRadian: 1.5*Math.PI,
        rotationRadian: 0,
        sweepRadian
      },
    )
    
    return [path,text]
  }, [progress])
  
  useEffect(() => {
    let startMS: DOMHighResTimeStamp
    let previousTimestampMS: DOMHighResTimeStamp
    const fullDuration = ANIMATION_MS_FOR_100_PERCENT// * percent * 0.01
    
    function step(timestampMS: DOMHighResTimeStamp) {
      if (startMS === undefined) {
        startMS = timestampMS
      }
      const elapsedMS = timestampMS - startMS
      if (previousTimestampMS !== timestampMS) {
        const t = elapsedMS / fullDuration
        const progress =
          easeOutQuint(t)
          //easeOutCubic(t)
          //intOutQuadBlend(t)
          //parametricBlend(t)
          //bezierBlend(t)
        setProgress(progress)
      }
      
      if (elapsedMS < fullDuration) {
        previousTimestampMS = timestampMS
        requestAnimationFrame(step)    
      }
    }
    
    const id = requestAnimationFrame(step)
    return () => cancelAnimationFrame(id)
  }, [])
  
  return <div 
    className={classNames(
      "progress-circle",
        className, 
        {
          'correct': correct,
          'incorrect': !correct,
        }
    )}
  >
    <div className="progress-circle-content">
      <svg className="progress-circle-graphics" viewBox="0 0 100 100">
        <circle className="background" r="50" cx="50" cy="50" />
        <circle className="outline-full" r="43" cx="50" cy="50" />
        <path className="outline-percent"  d={path} />
      </svg>
      <p className="progress-circle-text">{text}%</p>
    </div>
  </div>
}


// http://xahlee.info/js/svg_circle_arc.html
function buildArcPath(ellipse: {
  center: { x: number, y: number },
  radius: { x: number, y: number },
  startAngleRadian: number,
  sweepRadian: number,
  rotationRadian: number,
}) {
  type v2 = [ number, number ];
  type mat3x2 = [ v2, v2 ];

  function matMultiply(m: mat3x2, pt: v2): v2 {
    return [
      m[0][0] * pt[0] + m[0][1] * pt[1],
      m[1][0] * pt[0]+ m[1][1] * pt[1]
    ]
  }

  function generateRotationMatrix(angle: number): mat3x2 {
    return [
      [Math.cos(angle),-Math.sin(angle)], [Math.sin(angle), Math.cos(angle)]
    ]
  }

  function vecAdd(a: v2, b: v2): v2 {
    return [a[0] + b[0], a[1] + b[1]]
  }

  const sweep = ellipse.sweepRadian % (2 * Math.PI);
  const rotMatrix = generateRotationMatrix(ellipse.rotationRadian);
  const rx = ellipse.radius.x;
  const ry = ellipse.radius.y;
  const t1 = ellipse.startAngleRadian;
  const center: v2 = [ellipse.center.x, ellipse.center.y];

  const [sX, sY] = ( vecAdd ( matMultiply ( rotMatrix, [rx * Math.cos(t1), ry * Math.sin(t1)] ), center ) );
  const [eX, eY] = ( vecAdd ( matMultiply ( rotMatrix, [rx * Math.cos(t1+sweep), ry * Math.sin(t1+sweep)] ), center ) );
  const fA = ( (  sweep > Math.PI ) ? 1 : 0 );
  const fS = ( (  sweep > 0 ) ? 1 : 0 );

  const rotationDegrees = ellipse.rotationRadian / (2*Math.PI) *360;

  const path = `M ${sX} ${sY} A ${rx} ${ry} ${rotationDegrees} ${fA} ${fS} ${eX} ${eY}`;
  return path;
}

